import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  IconButton,
  Typography,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Tab, TabFormData, DialogMode } from "../types";
import IconPicker from "./IconPicker/IconPicker";
import ColorPicker from "./Theme/ColorPicker";
import ShareManager from "./Dashboard/ShareManager";
import SharedWithDisplay from "./Dashboard/SharedWithDisplay";
import IconDisplay from "./IconDisplay/IconDisplay";
import { getTileIcon } from "../utils/logoDetection";

interface TabEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TabFormData) => Promise<void>;
  tab?: Tab | null;
  mode: DialogMode;
  isLoading?: boolean;
}

const validationSchema = yup.object({
  name: yup
    .string()
    .required("Tab name is required")
    .min(1, "Tab name must be at least 1 character")
    .max(50, "Tab name must be less than 50 characters"),
  icon: yup.string().required("Icon is required"),
  color: yup
    .string()
    .required("Color is required")
    .matches(/^#[0-9A-F]{6}$/i, "Invalid color format"),
  iconType: yup.string().oneOf(["material", "service", "custom"]).optional(),
  iconUrl: yup.string().optional(),
});

const defaultFormData: TabFormData = {
  name: "",
  icon: "tab",
  color: "#1976d2",
  iconType: 'service',
  iconUrl: 'https://www.google.com/s2/favicons?sz=32&domain=example.com',
};

const TabEditDialog: React.FC<TabEditDialogProps> = ({
  open,
  onClose,
  onSubmit,
  tab,
  mode,
  isLoading = false,
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [shareRefreshKey, setShareRefreshKey] = useState(0);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [autoDetectedIcon, setAutoDetectedIcon] = useState<{type: string, value: string, fallback?: string} | null>(null);
  const [isManualIcon, setIsManualIcon] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
  } = useForm<TabFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormData,
    mode: "onChange",
  });

  // Watch form values for preview
  const watchedValues = watch();
  const watchedIcon = watch('icon');
  const watchedIconType = watch('iconType');
  const watchedIconUrl = watch('iconUrl');

  useEffect(() => {
    if (open && tab && mode === "edit") {
      reset({
        name: tab.name,
        icon: tab.icon,
        color: tab.color,
        iconUrl:tab.iconUrl,
        iconType:tab.iconType
      });
      // Set manual icon state if editing existing tab - always true for editing to show all options
      setIsManualIcon(true);
    } else if (open && mode === "create") {
      reset(defaultFormData);
      setIsManualIcon(true);
    }
    setSubmitError(null);
    setSubmitSuccess(false);
  }, [open, tab, mode, reset]);


  const handleFormSubmit = async (data: TabFormData) => {
    try {
      setSubmitError(null);
      await onSubmit(data);
      setSubmitSuccess(true);

      // Close dialog after brief success message
      setTimeout(() => {
        onClose();
        setSubmitSuccess(false);
      }, 1000);
    } catch (error: any) {
      console.error("Tab submit error:", error);
      setSubmitError(error.message || "Failed to save tab");
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setSubmitError(null);
      setSubmitSuccess(false);
      setIconPickerOpen(false);
      setShowIconPicker(false);
    }
  };

  const handleIconSelect = (
    iconName: string,
    iconType?: "material" | "service" | "custom",
    iconUrl?: string
  ) => {
    setValue("icon", iconName, { shouldDirty: true });
    setValue("iconType", iconType || 'material', { shouldDirty: true });
    if (iconType === 'service' || iconType === 'custom') {
      setValue("iconUrl", iconUrl || '', { shouldDirty: true });
    } else {
      setValue("iconUrl", '', { shouldDirty: true });
    }
    setIsManualIcon(true);
    setShowIconPicker(false);
    setIconPickerOpen(false);
  };

  const handleShareSuccess = () => {
    setShareRefreshKey((prev) => prev + 1);
    setSubmitSuccess(true);
    setTimeout(() => setSubmitSuccess(false), 3000);
  };

  const isCreateMode = mode === "create";
  const dialogTitle = isCreateMode ? "Create New Tab" : "Edit Tab";
  const submitButtonText = isCreateMode ? "Create Tab" : "Save Changes";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div">
          {dialogTitle}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isLoading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <DialogContent dividers>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tab {isCreateMode ? "created" : "updated"} successfully!
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Tab Preview */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: "background.default",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Chip
                  icon={<span style={{ color: watchedValues.color }}>●</span>}
                  label={watchedValues.name || "Tab Name"}
                  variant="outlined"
                  sx={{
                    borderColor: watchedValues.color,
                    color: watchedValues.color,
                    "& .MuiChip-icon": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Tab Name */}
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Tab Name"
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={isLoading}
                    placeholder="Enter tab name"
                  />
                )}
              />
            </Grid>

            {/* Icon Selection */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tab Icon
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Icon preview */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconDisplay
                      icon={watchedIcon || 'tab'}
                      iconType={watchedIconType as 'material' | 'service' | 'custom'}
                      iconUrl={watchedIconUrl}
                      url=""
                      title=""
                      size={32}
                      fallbackIcon="Tab"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {autoDetectedIcon && !isManualIcon ? '(Auto-detected)' : '(Manual)'}
                    </Typography>
                  </Box>
                  
                  {/* Manual selection button */}
                  <Button
                    variant="outlined"
                    onClick={() => setShowIconPicker(true)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    disabled={isLoading}
                  >
                    {isManualIcon ? 'Change Icon' : 'Override Icon'}
                  </Button>
                  
                  {/* Reset to auto-detect button */}
                  {isManualIcon && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setIsManualIcon(false);
                        // Reset to default tab icon
                        setValue('icon', 'tab', { shouldDirty: true });
                        setValue('iconType', 'material', { shouldDirty: true });
                        setValue('iconUrl', '', { shouldDirty: true });
                      }}
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      Use Default
                    </Button>
                  )}
                </Box>
                
                {/* Editable favicon URL field - always visible */}
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="iconUrl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        label="Icon URL (Optional)"
                        placeholder="https://www.google.com/s2/favicons?sz=32&domain=example.com"
                        helperText={
                          watchedIconType === 'material' 
                            ? "Add a URL to use a custom favicon instead of the material icon"
                            : "Edit the favicon URL or use a custom image URL"
                        }
                        disabled={isLoading}
                        onChange={(e) => {
                          field.onChange(e);
                          setIsManualIcon(true); // Mark as manually edited
                          // If URL is provided, switch to service icon type, otherwise keep current type
                          if (e.target.value.trim()) {
                            setValue('iconType', 'service', { shouldDirty: true });
                            setValue('icon', e.target.value, { shouldDirty: true }); // Update icon value to match
                          } else if (watchedIconType === 'service') {
                            // If URL is cleared and was service type, revert to material
                            setValue('iconType', 'material', { shouldDirty: true });
                            setValue('icon', 'tab', { shouldDirty: true }); // Reset to default material icon
                          }
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Color Selection */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Controller
                  name="color"
                  control={control}
                  render={({ field }) => (
                    <ColorPicker
                      value={field.value}
                      onChange={field.onChange}
                      label="Tab Color"
                      disabled={isLoading}
                    />
                  )}
                />
                {errors.color && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {errors.color.message}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Share Section - Only show in edit mode */}
            {mode === "edit" && tab && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />

                {/* Show who the tab is currently shared with */}
                <Box sx={{ mb: 3 }}>
                  <SharedWithDisplay
                    key={`shared-with-${shareRefreshKey}`}
                    tabId={tab.id}
                    tabName={watchedValues.name || tab.name}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Share Manager for new sharing */}
                <ShareManager
                  key={shareRefreshKey}
                  tabId={tab.id}
                  tabName={watchedValues.name || tab.name}
                  onShareSuccess={handleShareSuccess}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !isValid || (!isDirty && mode === "edit")}
            startIcon={<SaveIcon />}
          >
            {isLoading ? "Saving..." : submitButtonText}
          </Button>
        </DialogActions>
      </form>

      {/* Icon Picker Dialog */}
      <IconPicker
        open={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onIconSelect={handleIconSelect}
        selectedIcon={watchedValues.icon}
      />
    </Dialog>
  );
};

export default TabEditDialog;
